// Раздел «Буи»: карта in-situ платформ и карточка одной из них.
//
// Устроен по образцу «Спутников»: своя карта, свой слой, свои фильтры, карточка
// справа. Отличие одно и содержательное — платформы не считаются на лету из
// орбиты, а лежат в базе, поэтому слой спрашивается ПО КАДРУ: платформ тысячи,
// и мировой запрос отдавал бы то, чего человек всё равно не видит.

import { domainMaxBounds } from '../domain/limits'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import type { BuoyPlatformsResponse } from '../api'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { createStyleGate } from '../map/styleGate'
import { useMapView } from '../map/mapView'
import { useT } from '../i18n'
import { buoyIconExpr } from './buoyLayer'
import { ensureBuoyIcons } from './buoyIcons'
import { applyFilter, toggleType, visibleCount } from './buoyFilter'
import type { BuoyCollection, BuoyFilter } from './buoyFilter'
import { parseBuoyUrl, writeBuoyUrl } from './buoyUrl'
import { haloColorExpr, haloFilter } from './buoyResidual'
import { trackLine } from './trackStyle'
import { FilterPanel } from './FilterPanel'
import { BuoyCard } from './BuoyCard'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { boxKey, boundsToBox, useBoundaryOverlays } from '../map/useBoundaryOverlays'

const SOURCE_ID = 'buoy-platforms'
const LAYER_ID = 'buoy-platforms-layer'
const HALO_LAYER_ID = 'buoy-residual-halo'
const TRACK_SOURCE_ID = 'buoy-track'
const TRACK_LAYER_ID = 'buoy-track-line'

const EMPTY_FC: BuoyCollection = { type: 'FeatureCollection', features: [] }

// Дальневосточный вид по умолчанию: раздел про наши акватории, и мировой вид
// открывался бы пустым океаном.
const HOME_CENTER: [number, number] = [152, 48]
const HOME_ZOOM = 3.4

export function BuoysPage() {
  const t = useT()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const gateRef = useRef(createStyleGate())

  const [searchParams, setSearchParams] = useSearchParams()
  const bootRef = useRef<ReturnType<typeof parseBuoyUrl>>()
  if (!bootRef.current) bootRef.current = parseBuoyUrl(searchParams)
  const boot = bootRef.current

  const { center, zoom, onMoveEnd } = useMapView()
  const moveEndRef = useRef(onMoveEnd)
  moveEndRef.current = onMoveEnd
  const initialViewRef = useRef({ center: center ?? HOME_CENTER, zoom: zoom ?? HOME_ZOOM })

  const [filter, setFilter] = useState<BuoyFilter>({
    types: boot.types,
    hideStale: boot.hideStale,
  })
  const [selected, setSelected] = useState<string | null>(boot.selected)
  const [residual, setResidual] = useState<string | null>(boot.residual)
  const [eez, setEez] = useState(boot.eez)
  const [fd, setFd] = useState(boot.fd)
  const [box, setBox] = useState<ReturnType<typeof boundsToBox> | null>(null)

  // Единственный писатель раздела в URL; вид карты ведёт useMapView своим
  // экземпляром useSearchParams, поэтому трогаем только свои ключи.
  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        for (const key of ['types', 'buoy', 'fresh', 'resid', 'eez', 'fd']) next.delete(key)
        writeBuoyUrl({
          types: filter.types, selected, hideStale: filter.hideStale, residual, eez, fd,
        }).forEach((value, key) => next.set(key, value))
        return next
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selected, residual, eez, fd])

  // Рубежи — общим хуком, как на всех картах со своей сборкой. Кладутся ПОД
  // трек и символы платформ (`beforeId`): линия поверх них перечёркивала бы
  // ровно то, ради чего раздел и открыт. Ключ кадра — сама РАМКА: она и так
  // пересчитывается на каждый `moveend` этой страницы.
  const boundaries = { eez, districts: fd }
  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: boundaries, viewKey: boxKey(box), beforeId: TRACK_LAYER_ID,
  })

  const platformsQuery = useQuery({
    // Слой невязки — ЧАСТЬ КЛЮЧА: включив раскраску, кабинет обязан получить
    // ответ с невязкой, а не прежний, взятый из кэша без неё.
    queryKey: ['buoys', 'platforms', box, residual],
    queryFn: () => cabinetApi.buoys.platforms(box!, undefined, residual),
    enabled: box != null,
    // Свежесть платформ меняется минутами, а не секундами: чаще спрашивать
    // незачем, реже — карта врала бы возрастом.
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  })

  const cardQuery = useQuery({
    queryKey: ['buoys', 'card', selected],
    queryFn: () => cabinetApi.buoys.card(selected!),
    enabled: selected != null,
  })

  const collection: BuoyCollection = useMemo(() => {
    const data = platformsQuery.data as BuoyPlatformsResponse | undefined
    if (!data) return EMPTY_FC
    return {
      type: 'FeatureCollection',
      features: data.features as unknown as BuoyCollection['features'],
    }
  }, [platformsQuery.data])

  const filtered = useMemo(() => applyFilter(collection, filter), [collection, filter])

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      // Подпись рисуем сами: встроенный блок MapLibre пишет своё имя в тот же
      // угол и лезет под знак поставщика — см. attributionControl.test.ts
      attributionControl: false,
      center: initialViewRef.current.center,
      zoom: initialViewRef.current.zoom,
      // Рамка домена: карта не уводится туда, где сервер откажет.
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    const gate = createStyleGate()
    gateRef.current = gate

    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')

    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndRef.current({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      setBox(boundsToBox(map))
    })

    map.on('load', () => {
      ensureBuoyIcons(map)
      if (!map.getSource(TRACK_SOURCE_ID)) {
        map.addSource(TRACK_SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
      }
      if (!map.getLayer(TRACK_LAYER_ID)) {
        map.addLayer({
          id: TRACK_LAYER_ID,
          type: 'line',
          source: TRACK_SOURCE_ID,
          paint: {
            'line-color': '#38bdf8',
            'line-width': 1.6,
            // Пунктир по редким фиксам: промежутка между всплытиями мы не
            // наблюдали, и сплошная линия утверждала бы путь, которого нет.
            'line-dasharray': ['case', ['get', 'sparse'], ['literal', [2, 2]], ['literal', [1, 0]]] as never,
          },
        })
      }
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC })
      }
      // Ореол невязки ложится ПОД силуэт: форма по-прежнему говорит про тип,
      // силуэт — про возраст, ореол — про расхождение с моделью. Фильтр по
      // НАЛИЧИЮ свойства: точка, которую не с чем сравнить, ореола не
      // получает вовсе — ноль на его месте похвалил бы модель за молчание.
      if (!map.getLayer(HALO_LAYER_ID)) {
        map.addLayer({
          id: HALO_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          filter: haloFilter() as never,
          layout: { visibility: 'none' },
          paint: {
            'circle-radius': 11,
            'circle-color': haloColorExpr('sst') as never,
            'circle-opacity': 0.85,
            'circle-stroke-width': 1,
            'circle-stroke-color': 'rgba(15,23,42,0.55)',
          },
        })
      }
      if (!map.getLayer(LAYER_ID)) {
        map.addLayer({
          id: LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          layout: {
            'icon-image': buoyIconExpr() as never,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-size': 0.85,
          },
        })
      }

      map.on('mouseenter', LAYER_ID, () => {
        map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', LAYER_ID, () => {
        map.getCanvas().style.cursor = ''
      })
      map.on('click', LAYER_ID, e => {
        const id = e.features?.[0]?.properties?.platform_id
        if (id != null) setSelected(String(id))
      })

      setBox(boundsToBox(map))
      gate.markReady()
    })

    // Смена языка кабинета меняет АДРЕС тайла подложки (у Яндекса `lang=`),
    // а карта строится один раз — учёт довозит новый адрес до неё.
    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Отфильтрованная коллекция → источник. Через шлюз: приход до разбора стиля
  // иначе терялся бы молча (та же ловушка, что у «Спутников»).
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      source?.setData(filtered as never)
    })
  }, [filtered])

  // Трек выбранной платформы. Долгота приводится к обороту мира ЦЕНТРА КАДРА в
  // момент отрисовки: прочитанная раньше, за 180-м меридианом она увела бы
  // линию на целый мир от кадра.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const points = cardQuery.data?.track ?? []
    gateRef.current.run(() => {
      const source = map.getSource(TRACK_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      if (!source) return
      const lonMin = map.getBounds().getWest()
      const line = trackLine(points, lonMin)
      source.setData(
        line
          ? ({ type: 'FeatureCollection', features: [line] } as never)
          : ({ type: 'FeatureCollection', features: [] } as never),
      )
    })
  }, [cardQuery.data])

  // Шкала у каждого слоя своя (±2 °C против ±0,4 psu), поэтому цвет ореола
  // переписывается при смене слоя, а не задаётся раз при создании карты.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      if (!map.getLayer(HALO_LAYER_ID)) return
      map.setPaintProperty(HALO_LAYER_ID, 'circle-color',
        haloColorExpr(residual ?? 'sst') as never)
      map.setLayoutProperty(HALO_LAYER_ID, 'visibility', residual ? 'visible' : 'none')
    })
  }, [residual])

  const onToggleType = useCallback((type: string) => {
    setFilter(prev => toggleType(prev, type))
  }, [])

  const onToggleStale = useCallback(() => {
    setFilter(prev => ({ ...prev, hideStale: !prev.hideStale }))
  }, [])

  const meta = (platformsQuery.data as BuoyPlatformsResponse | undefined)?.meta

  // Сколько точек кадра ВООБЩЕ получили невязку. Число говорит то, чего не
  // говорит карта: пустой кадр без единого ореола иначе неотличим от
  // «модель везде точна» — а на деле сравнивать чаще всего не с чем (замер
  // прода 26.08.2026: измерение поверхности за сутки есть у 58 платформ из 170).
  const scored = useMemo(
    () => collection.features.filter(f => f.properties?.resid != null).length,
    [collection],
  )

  return (
    <div className="relative h-full w-full">
      {/* Высота, а НЕ `absolute inset-0`: MapLibre вешает на контейнер свой
          класс `.maplibregl-map { position: relative }`, который перебивает
          tailwind-класс `absolute`. Тогда `inset-0` перестаёт задавать размер,
          высота схлопывается в ноль, и карта не видна вообще — при живых
          данных и без единой ошибки в консоли. Сторож — `mapContainer.test.ts`. */}
      <div ref={containerRef} className="h-full w-full" />
      {/* Подпись подложки — требование условий поставщика: у Яндекса знак
          обязан стоять в углу карты и вести на Яндекс Карты, у Carto
          обязана быть видна атрибуция OSM и CARTO. */}
      <BasemapAttribution corner="bl" />

      <FilterPanel
        facets={meta?.types ?? []}
        filter={filter}
        shown={visibleCount(collection, filter)}
        retired={meta?.retired ?? 0}
        onToggleType={onToggleType}
        onToggleStale={onToggleStale}
        residual={residual}
        onResidual={setResidual}
        scored={scored}
        boundaries={boundaries}
        onToggleBoundary={key => (key === 'eez' ? setEez(v => !v) : setFd(v => !v))}
        boundaryMeta={boundaryMeta}
      />

      {platformsQuery.isError && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-xl border border-hull bg-abyss/95 px-3 py-1 text-[12px] text-mist shadow-console backdrop-blur">
          {t('buoys.error.platforms')}
        </div>
      )}

      {selected && cardQuery.data && (
        <div className="pointer-events-none absolute bottom-3 right-3 z-10">
          <BuoyCard data={cardQuery.data} onClose={() => setSelected(null)} />
        </div>
      )}
    </div>
  )
}
